/**
 * DashboardGrid — Bento 12-column layout for the Hantavirus dashboard.
 *
 * Composes BentoGrid with named widget slots so callers declare layout intent
 * separately from widget implementation. Server-driven slot ordering is
 * accepted via `layout` prop; falls back to a sensible priority default.
 *
 * Design rules (uncodixify-ui.md):
 *   - No hover lift, no shadow escalation on the grid wrapper.
 *   - Slots impose no extra chrome — each widget controls its own surface.
 *
 * RTL rules (rtl-support-arabic.md):
 *   - Grid columns auto-reverse under dir="rtl"; no rtl:flex-row-reverse.
 *
 * Data policy:
 *   - No mock/placeholder children; all data flows from parent page props.
 *   - EmptyState uses live sourceName from the widget's data hook.
 */

import type { ReactNode } from "react";
import { BentoGrid, BentoItem, type BentoSpan } from "./BentoGrid.js";
import { WidgetErrorBoundary } from "./errors/WidgetErrorBoundary.js";
import { DegradedWidgetWrapper } from "./offline/DegradedWidgetWrapper.js";
import type { TranslateFn } from "#/lib/i18n.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A named slot ID matching the dashboard widget inventory. */
export type SlotId =
  | "kpi"
  | "ticker"
  | "map"
  | "world-status"
  | "vessel-track"
  | "port-events"
  | "regions"
  | "timeline"
  | "passenger-silence"
  | "communication-patterns"
  | "contradictions"
  | "unanswered-questions"
  | "news"
  | "faq"
  | "pathogen"
  | "exposure-window"
  | "official-sources"
  | "custom";

export interface SlotConfig {
  id: SlotId;
  span: BentoSpan;
  /** ARIA label forwarded to the BentoItem wrapper. */
  ariaLabel?: string;
  /** `id` attribute forwarded to BentoItem for skip-link targets. */
  htmlId?: string;
}

export interface DashboardGridProps {
  /** Widget nodes keyed by SlotId. Unmapped keys are silently ignored. */
  slots: Partial<Record<SlotId, ReactNode>>;
  /**
   * Ordered slot configs. Defaults to `DEFAULT_LAYOUT`.
   * Pass a server-fetched layout for personalised ordering.
   */
  layout?: SlotConfig[];
  /** Additional class names on the outer container. */
  className?: string;
  /** Translate function for error boundary labels. */
  t: TranslateFn;
  /** When true, wraps each widget in a degraded (muted/grayscale) overlay. */
  isDegraded?: boolean;
}

// ---------------------------------------------------------------------------
// Default layout (priority order, responsive spans)
// ---------------------------------------------------------------------------

export const DEFAULT_LAYOUT: SlotConfig[] = [
  // Full-width KPI strip
  {
    id: "kpi",
    span: { base: 12 },
    htmlId: "kpis",
    ariaLabel: "Key indicators",
  },
  // Ticker below KPI — full-width
  {
    id: "ticker",
    span: { base: 12 },
    htmlId: "ticker",
  },
  // Exposure window — full on mobile, 2/3 on desktop
  {
    id: "exposure-window",
    span: { base: 12, lg: 8 },
    htmlId: "exposure-window",
  },
  // Pathogen card — sidebar on desktop
  {
    id: "pathogen",
    span: { base: 12, lg: 4 },
    htmlId: "pathogen",
  },
  // Vessel live track — full-width
  {
    id: "vessel-track",
    span: { base: 12 },
    htmlId: "vessel-live-track",
  },
  // Outbreak map — full-width
  {
    id: "map",
    span: { base: 12 },
    htmlId: "map",
  },
  // Country status map (2/3) + Newswire (1/3)
  {
    id: "world-status",
    span: { base: 12, lg: 8 },
    htmlId: "world-status-map",
  },
  // Port events — full-width
  {
    id: "port-events",
    span: { base: 12 },
    htmlId: "port-events",
  },
  // Regional grid — full-width
  {
    id: "regions",
    span: { base: 12 },
    htmlId: "regions",
  },
  // Timeline — full-width
  {
    id: "timeline",
    span: { base: 12 },
    htmlId: "timeline",
  },
  // Passenger silence (2/3) + Influencer spotlight (1/3)
  {
    id: "passenger-silence",
    span: { base: 12, lg: 8 },
    htmlId: "passenger-silence",
  },
  // Communication patterns — full-width
  {
    id: "communication-patterns",
    span: { base: 12 },
  },
  // Contradictions — full-width
  {
    id: "contradictions",
    span: { base: 12 },
  },
  // Unanswered questions — full-width
  {
    id: "unanswered-questions",
    span: { base: 12 },
  },
  // News (2/3) + FAQ (1/3) — sticky FAQ on desktop
  {
    id: "news",
    span: { base: 12, lg: 8 },
    htmlId: "news",
    ariaLabel: "News",
  },
  {
    id: "faq",
    span: { base: 12, lg: 4 },
    htmlId: "faq",
    ariaLabel: "FAQ",
  },
  // Official sources — full-width footer slot
  {
    id: "official-sources",
    span: { base: 12 },
  },
];

// ---------------------------------------------------------------------------
// DashboardGrid
// ---------------------------------------------------------------------------

export function DashboardGrid({
  slots,
  layout = DEFAULT_LAYOUT,
  className = "",
  t,
  isDegraded = false,
}: DashboardGridProps) {
  return (
    <BentoGrid className={className}>
      {layout.map((config) => {
        const node = slots[config.id];
        if (!node) return null;
        return (
          <BentoItem
            key={config.id}
            span={config.span}
            id={config.htmlId}
            aria-label={config.ariaLabel}
          >
            <WidgetErrorBoundary
              widgetName={config.id}
              t={t}
              resetKeys={[config.id]}
            >
              <DegradedWidgetWrapper isDegraded={isDegraded}>
                {node}
              </DegradedWidgetWrapper>
            </WidgetErrorBoundary>
          </BentoItem>
        );
      })}
    </BentoGrid>
  );
}
